import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { ReactComponent as NovaIcon } from "resources/icons/nova.svg";

import ConversationCards from "./ConversationCards";
import NovaChatOptions from "./NovaChatOptions";
import MessageBubble from "./MessageBubble";
import RegenerateAiResponse from "./RegenerateAiResponse";
import NovaAiLoader from "./NovaAiLoader";
import NovaAiInitialLoader from "./NovaAiInitialLoader";
import useNovaAiModalBody from "./hooks/useNovaAiModalBody";
import { selectChatMessages } from "utils/getNovaConversationDetails";

const CreateWithNovaAiModalBody = (props) => {
  const {
    showHistory,
    showDefaultPrompts,
    product,
    messageText,
    setMessageText,
    isNewConversation,
    sentMessageText,
    isAiResponseLoading,
    isDraggedToEdge,
    dir,
  } = props;
  const {
    setScrollContainer,
    latestAssistantMessage,
    conversationId,
    userMessageId,
    handleScroll,
    regenerateCurrentResponse,
    sendNovaChatFeedback,
    lastScrollDirection,
    isAiLoading,
    scrollToBottom,
    fromRegenerate,
  } = useNovaAiModalBody(props);

  const userNovaChatMessages = useSelector((state) =>
    selectChatMessages(state, conversationId)
  );
  const regenerateAiResponse = useSelector(
    (state) =>
      state?.aiFeatures?.goalsCreateWithNovaAI?.regenerateAiResponse?.[
        conversationId
      ]
  );
  const bottomRef = useRef(null);
  const [pendingMessages, setPendingMessages] = useState([]);

  useEffect(() => {
    if (isAiResponseLoading && sentMessageText) {
      setPendingMessages((prev) => [
        ...prev,
        { role: "user", content: sentMessageText, fromRegenerate },
      ]);
    } else {
      setPendingMessages([]);
    }
  }, [isAiResponseLoading, sentMessageText, fromRegenerate]);

  useEffect(() => {
    if (
      scrollToBottom &&
      (isAiLoading ||
        isAiResponseLoading ||
        userNovaChatMessages?.length > 0) &&
      bottomRef.current
    ) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [
    userNovaChatMessages,
    isAiLoading,
    isAiResponseLoading,
    lastScrollDirection,
    scrollToBottom,
  ]);

  if (showHistory) {
    return <ConversationCards {...props} />;
  }

  const displayMessages = [
    ...(userNovaChatMessages || []),
    ...pendingMessages.filter((msg) => !msg.fromRegenerate),
  ];

  return (
    <div className="flex flex-col nova-ai-modal-body">
      {showDefaultPrompts && (
        <div className="nova-default-prompts">
          <NovaIcon className="display-inline nova-response-icon" />
          <NovaChatOptions
            product={product}
            isAiLoading={isAiLoading}
            messageText={messageText}
            setMessageText={setMessageText}
          />
        </div>
      )}
      <div
        ref={setScrollContainer}
        onScroll={handleScroll}
        className="flex flex-col user-nova-chat-wrapper"
      >
        {!isNewConversation && displayMessages.length > 0 ? (
          displayMessages.map((msg, index) => {
            const isLast = index === displayMessages.length - 1;
            const isPendingMessage =
              msg.id && String(msg.id).startsWith("pending-");

            return (
              <MessageBubble
                key={msg.id || index}
                messageText={msg.content}
                sentMessageText={sentMessageText}
                showUserMessage={msg.role === "user"}
                isAiLoading={false}
                aiResponse={
                  msg.role === "assistant" ? { text: msg.content } : null
                }
                sendNovaChatFeedback={sendNovaChatFeedback}
                conversationId={conversationId}
                aiMessageId={msg.role === "assistant" ? msg.id : null}
                alwaysShowIcons={
                  msg.role === "assistant" &&
                  msg.id === latestAssistantMessage?.id
                }
                ref={isLast ? bottomRef : null}
                isDraggedToEdge={isDraggedToEdge}
                dir={dir}
                isPending={isPendingMessage}
              />
            );
          })
        ) : !isNewConversation &&
          !showDefaultPrompts &&
          displayMessages.length === 0 ? (
          <NovaAiInitialLoader />
        ) : null}

        {isAiResponseLoading && <NovaAiLoader bottomRef={bottomRef} />}

        {regenerateAiResponse && (
          <RegenerateAiResponse
            isAiLoading={isAiLoading}
            sentMessageText={
              pendingMessages.find((m) => m.fromRegenerate)?.content
            }
            regenerateCurrentResponse={regenerateCurrentResponse}
            userMessageId={userMessageId}
          />
        )}
      </div>
    </div>
  );
};

export default CreateWithNovaAiModalBody;
